import type { ColumnFiltersState } from "@tanstack/react-table";
import { multiSelectParam } from "@/useCases/serverTableQuery";

type TBpOption = { value: string; label: string };
type TSubOption = { value: string; label: string; businessPartnerId?: string };

type TFacets = {
    businessPartnerOptions: TBpOption[];
    submissionOptions: TSubOption[];
};

/**
 * Narrows Business Partner and Submission filter dropdowns based on each other's
 * current selection, without any extra server requests.
 *
 * Rules:
 * - If business partners are selected, submissions are narrowed to those belonging
 *   to the selected partners (plus any already-selected submissions, so they stay
 *   visible and can be deselected).
 * - If submissions are selected, business partners are narrowed to those that own
 *   the selected submissions (plus any already-selected partners).
 * - A dimension's own selection never restricts its own list (so more values can
 *   be added).
 */
export function narrowFileFacets(facets: TFacets, columnFilters: ColumnFiltersState): TFacets {
    const selectedBpIds = new Set(multiSelectParam(columnFilters, "serviceProviderName") ?? []);
    const selectedSubIds = new Set(multiSelectParam(columnFilters, "dealName") ?? []);

    const submissionOptions =
        selectedBpIds.size > 0
            ? facets.submissionOptions.filter(
                  (s) =>
                      (s.businessPartnerId != null && selectedBpIds.has(s.businessPartnerId)) ||
                      selectedSubIds.has(s.value),
              )
            : facets.submissionOptions;

    const narrowedBpIds =
        selectedSubIds.size > 0
            ? new Set(
                  facets.submissionOptions
                      .filter((s) => selectedSubIds.has(s.value) && s.businessPartnerId != null)
                      .map((s) => s.businessPartnerId as string),
              )
            : null;

    const businessPartnerOptions =
        narrowedBpIds != null
            ? facets.businessPartnerOptions.filter(
                  (bp) => narrowedBpIds.has(bp.value) || selectedBpIds.has(bp.value),
              )
            : facets.businessPartnerOptions;

    return { businessPartnerOptions, submissionOptions };
}
